// Datei: Client/src/store/taskStore.ts
//
// KOMPLETT ÜBERARBEITET: Jetzt werden Aufgaben korrekt als "überfällig" markiert,
// wenn Datum + Uhrzeit bereits in der Vergangenheit liegen. Außerdem wird für
// Tasks, bei denen prevent_overlapping aktiv ist, nur die jeweils neueste
// überfällige Aufgabe angezeigt; ältere werden per API-Call versteckt.
//
// WICHTIG: Damit hideTaskOnServer funktioniert, muss der Benutzer authentifiziert sein.
// Der Token wird zentral über den SessionManager verwaltet.
//
// ---------------------------------------------------------------------------
// DEUTSCHE ERKLÄRUNG:
//   Der Hauptunterschied zur vorherigen Version besteht darin, dass wir in
//   parseDueDateTime nun explizit eine lokale Datum-Uhrzeit-Berechnung vornehmen,
//   anstatt parseISO zu verwenden, da das je nach Zeitzone zu Ungenauigkeiten
//   führen kann. Jetzt wird year, month, day, hours, minutes, seconds manuell
//   zusammengebaut, sodass wir sicherstellen, dass "2025-03-03 08:00" in der
//   lokalen Zeitzone auch wirklich 08:00 Uhr ist.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import { AllRoutesTypes } from '../types/AllRoutesTypes';
import { fetchWithAuth } from '../store/fetchClient';
import { useAuthStore } from '../store/authStore';
import appSocketService from '../services/AppSocketService';
import { httpClient } from '../core/SessionManager';
import { isRolliDueTodayOrOverdue } from '../utils/rolliDue';
import {
  format,
  addDays,
  addMinutes,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfToday,
} from 'date-fns';

/* -------------------------------------------------------------------------
 * Zusätzliche Datentypen für die Planner-Items (MHD, Zählung, Umbau)
 * -------------------------------------------------------------------------*/
type MhdItem = {
  storeLayoutId: string;
  bausteinId: string;
  layoutBausteinName: string;
  due_date: string;
  mhd_until_days?: number;
  overdue: boolean;
  last_check: string | null;
  prevent_overlapping?: boolean;
};

type ZaehlungItem = {
  storeLayoutId: string;
  bausteinId: string;
  layoutBausteinName: string;
  planned_date: string;
  overdue: boolean;
};

type UmbauItem = {
  storeLayoutId: string;
  bausteinId: string;
  layoutBausteinName: string;
  planned_date: string;
  overdue: boolean;
};

/* -------------------------------------------------------------------------
 * Struktur der Antwort von /api/feallig
 * -------------------------------------------------------------------------*/
type FaelligeDingeResponse = {
  tasks: AllRoutesTypes.TasksAPI.TaskItem[];
  mhd: MhdItem[];
  zaehlung: ZaehlungItem[];
  umbau: UmbauItem[];
};

type RolliApiItem = {
  id: string;
  name: string;
  lastDoneDate?: string | null;
  naechsteFaelligkeitDate?: string | null;
  require_sign?: boolean;
  require_time?: boolean;
  zusatzaufgaben?: Array<{
    id: string;
    title?: string;
    last_completed_at?: string | null;
  }>;
  zusatzaufgabenWarning?: 'none' | 'warning' | 'critical';
};

type TaskSocketPayload = {
  type?: 'create' | 'update' | 'completed' | 'delete' | string;
  entityId?: string;
  storeId?: string;
  task?: Task;
};

type RolliTimeEstimateEntry = {
  defektur?: { medianMinutes?: number | null };
  regalpflege?: { medianMinutes?: number | null };
  totalMedianMinutes?: number | null;
};

type RolliTimeEstimatesPayload = {
  estimates?: Record<string, RolliTimeEstimateEntry>;
  scope?: 'store' | 'cross_market' | null;
  computedAt?: string | null;
};

const isTaskSocketPayload = (value: unknown): value is TaskSocketPayload =>
  typeof value === 'object' && value !== null;

const isRolliTimeEstimatesPayload = (value: unknown): value is RolliTimeEstimatesPayload =>
  typeof value === 'object' && value !== null;

/* -------------------------------------------------------------------------
 * Erweitertes Task-Objekt im Store
 * -------------------------------------------------------------------------*/
type Task = AllRoutesTypes.TasksAPI.TaskItem & {
  plannerType?: 'mhd' | 'zählung' | 'umbau';
  plannerDate?: string;   // zusätzliches Feld für Planner
  isRolli?: boolean;      // Markierung für Rolli-Tasks
  rolliId?: string;       // ID des zugehörigen Rollis
  forceOverdue?: boolean; // erlaubt manuelles Überschreiben des "Überfällig"-Status
  formularTyp?: string | null; // Formulartyp-Zuordnung für die Task
  zusatzaufgabenWarning?: 'none' | 'warning' | 'critical'; // Warnung für überfällige Zusatzaufgaben

  // Zeitvorgaben (Median) für Rollis/Bereiche
  estimatedDefekturMinutes?: number | null;
  estimatedRegalpflegeMinutes?: number | null;
  estimatedTotalMinutes?: number | null;
  estimatesComputedAt?: string | null;
  estimatesScope?: 'store' | 'cross_market' | null;
};

/* -------------------------------------------------------------------------
 * Ladedaten-Status
 * -------------------------------------------------------------------------*/
interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

/* -------------------------------------------------------------------------
 * TaskState-Schnittstelle
 * -------------------------------------------------------------------------*/
interface TaskState {
  tasks: Task[];

  overdueTasks: Task[];
  mainTasks: Task[];

  loadingState: LoadingState;
  showCompleted: boolean;

  loadAllData: (storeId: string,filter? : string ,  futureDays?: number) => Promise<void>;
  fetchFaellige: (storeId: string,filter : string ,  futureDays?: number) => Promise<Task[]>;
  fetchRollis: (storeId: string) => Promise<Task[]>;

  setTasks: (tasks: Task[]) => void;
  addTask: (newTasks: Task[]) => void;
  upsertTaskFromServer: (task: Partial<Task> & { id: string }) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  deleteTask: (taskId: string) => void;
  toggleComplete: (taskId: string, skipPrepare?: boolean) => void;
  clearTasks: () => void;
  toggleShowCompleted: () => void;

  prepareDisplayedTasks: () => void;

  deletePlannerTask: (taskId: string) => void;
  deleteRolliTask: (taskId: string) => void;

  cleanupOldTasks: () => void;
}

/* -------------------------------------------------------------------------
 * Globale Konstanten
 * -------------------------------------------------------------------------*/
const ONE_HOUR = 60; // 60 Minuten
const SIX_MONTHS = 6;
const OVERDUE_BUFFER = 30; // Ab wann eine Aufgabe als überfällig gilt (Minuten-Puffer)

function resolveRolliZusatzaufgabenWarning(rolli: RolliApiItem): 'none' | 'warning' | 'critical' {
  if (rolli.zusatzaufgabenWarning) {
    return rolli.zusatzaufgabenWarning;
  }

  const zusatzaufgaben = Array.isArray(rolli.zusatzaufgaben) ? rolli.zusatzaufgaben : [];
  if (!zusatzaufgaben.length) {
    return 'none';
  }

  const now = Date.now();
  const twoWeeksAgo = now - 14 * 24 * 60 * 60 * 1000;
  const threeWeeksAgo = now - 21 * 24 * 60 * 60 * 1000;
  let warning: 'none' | 'warning' | 'critical' = 'none';

  for (const aufgabe of zusatzaufgaben) {
    if (!aufgabe.last_completed_at) {
      return 'critical';
    }

    const lastCompletedAt = new Date(aufgabe.last_completed_at).getTime();
    if (Number.isNaN(lastCompletedAt)) {
      return 'critical';
    }

    if (lastCompletedAt < threeWeeksAgo) {
      return 'critical';
    }

    if (lastCompletedAt < twoWeeksAgo) {
      warning = 'warning';
    }
  }

  return warning;
}

/* -------------------------------------------------------------------------
 * useTaskStore: Haupt-Store
 * -------------------------------------------------------------------------*/
export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  overdueTasks: [],
  mainTasks: [],
  loadingState: { isLoading: false, error: null },
  showCompleted: false,

  /**
   * loadAllData:
   * Lädt sämtliche Aufgaben (inkl. Planner-Items MHD, Zählung, Umbau) und Rollis.
   * 
   * - Ruft /api/feallig/<storeId> auf => liefert: tasks, mhd, zaehlung, umbau
   * - Ruft zusätzlich fetchRollis auf
   * - Zusammenführen => tasks
   * - prepareDisplayedTasks
   *
   * Optional: futureDays, um das Zeitfenster in die Zukunft zu erweitern.
   */
  loadAllData: async (storeId: string, filter = "incomplete", futureDays?: number) => {
    if (!storeId) return;
    set({ loadingState: { isLoading: true, error: null } });

    try {
      // 1) Reguläre Tasks + Planner-Tasks
      const tasksFaellig = await get().fetchFaellige(storeId, filter , futureDays);

      // 2) Rollis
      const rolliTasks = await get().fetchRollis(storeId);

      // Zusammenführen ohne Duplikate
      const merged = mergeById([...tasksFaellig, ...rolliTasks]);
      const mergedWithEstimates = await applyRolliTimeEstimatesToTasks(storeId, merged);
      set({ tasks: mergedWithEstimates });

      // Aufbereitung für Anzeige
      get().prepareDisplayedTasks();
    } catch (error) {
      console.error('Fehler in loadAllData:', error);
      set({ loadingState: { isLoading: false, error: String(error) } });
    } finally {
      set((s) => ({
        loadingState: { ...s.loadingState, isLoading: false },
      }));
    }
  },

  /**
   * fetchFaellige:
   * Lädt alle fälligen (und im definierten Zeitraum liegenden) Daten aus /api/feallig/<storeId>.
   * Gibt ein Array von Task-Objekten zurück, die
   * - Reguläre tasks
   * - MHD-Objekte
   * - Zählung-Objekte
   * - Umbau-Objekte
   * beinhalten.
   *
   * NEU: Optionaler Parameter futureDays, Standard = 7.
   */
  fetchFaellige: async (storeId: string,filter = "incomplete", futureDays?: number) => {
    if (!storeId) return [];

    try {
      // Standard: 7 Tage in die Zukunft
      const future = futureDays || 7;

      // Start in der Vergangenheit (z.B. 500 Tage zurück), um ältere Tasks nicht zu verpassen
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - 500);

      // End-Datum
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + future);

      const body = {
        start: format(pastDate, 'yyyy-MM-dd'),
        end: format(futureDate, 'yyyy-MM-dd'),
        filter: filter,
      };

      const res = await fetchWithAuth(`/api/feallig/${storeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error('Fehler beim Laden aus /api/feallig');
      }
      const data: FaelligeDingeResponse = await res.json();

      // 1) Reguläre Tasks
      const tasks: Task[] = data.tasks.map((t) => {
        const attachments = t.attachments ?? [];
        const description = t.description ?? extractDescriptionFromAttachments(attachments);
        const signatureInfo = normalizeSignatureInfo({
          hasSignature: t.hasSignature,
          signature_base64: t.signature_base64,
          signature_signed_at: t.signature_signed_at,
          signature_user_id: t.signature_user_id,
        });

        return {
          ...t,
          attachments,
          description: description ?? null,
          require_sign: t.require_sign ?? false,
          ...signatureInfo,
          // forceOverdue könnte hier gesetzt werden, falls das Backend ein bestimmtes Feld hat
          // In diesem Beispiel bleiben wir aber bei den Standardwerten.
        };
      });



      // 2) MHD-Items => plannerType = 'mhd'
      data.mhd.forEach((item) => {
        let plannerDate = item.due_date;
        if (item.mhd_until_days) {
          const today = new Date();
          const futureDate = addDays(today, item.mhd_until_days);
          plannerDate = format(futureDate, 'yyyy-MM-dd');
        }

        const plannerDateObj = new Date(plannerDate);
        const dueDatePlus7 = addDays(plannerDateObj, 7);
        const finalDueDate = format(dueDatePlus7, 'yyyy-MM-dd');

        const displayDueDate = format(new Date(item.due_date), 'dd.MM.yyyy');

        let title;
        if (item.last_check === null) {
          title = `MHD: ${item.layoutBausteinName} (noch nie geprüft!)`;
        } else if (item.overdue) {
          title = `MHD: ${item.layoutBausteinName} (überfällig seit ${displayDueDate})`;
        } else {
          title = `MHD: ${item.layoutBausteinName} (fällig am ${displayDueDate})`;
        }

        const isActuallyOverdue = isBefore(dueDatePlus7, startOfToday());

        tasks.push({
          id: `mhd_${item.storeLayoutId}_${item.bausteinId}`,
          title,
          completed: false,
          priority: item.overdue || isActuallyOverdue ? 'hoch' : 'mittel',
          due_time: null,
          plannerType: 'mhd',
          plannerDate: plannerDate,
          due_date: finalDueDate,
          prevent_overlapping: item.prevent_overlapping ?? true,
          forceOverdue: item.overdue || isActuallyOverdue,
          require_sign: false,
          hasSignature: false,
          description: null,
          signature_base64: null,
          signature_user_id: null,
          signature_signed_at: null,
        } as Task);
      });

      // 3) Zählung-Items => plannerType = 'zählung'
      const today = startOfToday();
      data.zaehlung.forEach((item) => {
        const plannedDateObj = new Date(item.planned_date);

        // Nur hinzufügen, wenn geplant <= heute (d.h. schon fällig)
        if (isAfter(today, plannedDateObj) || isEqual(today, plannedDateObj)) {
          const dueDatePlus7 = addDays(plannedDateObj, 7);
          const finalDueDate = format(dueDatePlus7, 'yyyy-MM-dd');

          const displayPlannedDate = format(plannedDateObj, 'dd.MM.yyyy');
          const isActuallyOverdue = isBefore(dueDatePlus7, today);
          const isOverdue = item.overdue || isActuallyOverdue;

          const title = isOverdue
            ? `Zählung: ${item.layoutBausteinName} (überfällig seit ${displayPlannedDate})`
            : `Zählung: ${item.layoutBausteinName} (fällig seit ${displayPlannedDate})`;

          tasks.push({
            id: `zaehlung_${item.storeLayoutId}_${item.bausteinId}`,
            title,
            completed: false,
            priority: isOverdue ? 'hoch' : 'mittel',
            due_time: null,
            plannerType: 'zählung',
            plannerDate: item.planned_date,
            due_date: finalDueDate,
            prevent_overlapping: true,
            forceOverdue: isOverdue,
            require_sign: false,
            hasSignature: false,
            description: null,
            signature_base64: null,
            signature_user_id: null,
            signature_signed_at: null,
          } as Task);
        }
      });

      // 4) Umbau-Items => plannerType = 'umbau'
      data.umbau.forEach((item) => {
        const plannedDateObj = new Date(item.planned_date);
        const dueDatePlus7 = addDays(plannedDateObj, 7);
        const finalDueDate = format(dueDatePlus7, 'yyyy-MM-dd');

        const displayPlannedDate = format(plannedDateObj, 'dd.MM.yyyy');
        const isActuallyOverdue = isBefore(dueDatePlus7, today);
        const isOverdue = item.overdue || isActuallyOverdue;

        const title = isOverdue
          ? `Umbau: ${item.layoutBausteinName} (überfällig seit ${displayPlannedDate})`
          : `Umbau: ${item.layoutBausteinName} (fällig seit ${displayPlannedDate})`;

        tasks.push({
          id: `umbau_${item.storeLayoutId}_${item.bausteinId}`,
          title,
          completed: false,
          priority: isOverdue ? 'hoch' : 'mittel',
          due_time: null,
          plannerType: 'umbau',
          plannerDate: item.planned_date,
          due_date: finalDueDate,
          prevent_overlapping: true,
          forceOverdue: isOverdue,
          require_sign: false,
          hasSignature: false,
          description: null,
          signature_base64: null,
          signature_user_id: null,
          signature_signed_at: null,
        } as Task);
      });

      return tasks;
    } catch (error) {
      console.error('fetchFaellige Fehler:', error);
      return [];
    }
  },

  /**
   * fetchRollis:
   * Beispiel-Implementierung, die nur Rollis lädt, deren dueDate == heute.
   * Rückgabe als Task-Array.
   * Erweitert um Zusatzaufgaben-Warnungen.
   */
  fetchRollis: async (storeId: string) => {
    if (!storeId) return [];
    try {
      const res = await fetchWithAuth(`/api/rollis?storeId=${storeId}&includeTaskMeta=1`);
      if (!res.ok) {
        throw new Error('Fehler beim Laden der Rollis');
      }
      const allRollis: RolliApiItem[] = await res.json();

      const today = startOfDay(new Date());
      const dueTodayOrOverdue = allRollis.filter((rolli) =>
        isRolliDueTodayOrOverdue(rolli, today)
      );

      // Zusatzaufgaben für jeden Rolli laden und Warnung berechnen
      const rolliTasks = dueTodayOrOverdue.map((rolli) => {
        const dueDateValue = rolli.naechsteFaelligkeitDate!;
        const dueDate = startOfDay(new Date(dueDateValue));
        const isOverdue = dueDate.getTime() < today.getTime();

        return {
          id: `rolli_${rolli.id}`,
          title: `Bereich: ${rolli.name}`,
          completed: false,
          priority: isOverdue ? 'hoch' : 'mittel',
          isRolli: true,
          rolliId: rolli.id,
          hidden: false,
          require_sign: rolli.require_sign || false,
          require_time: rolli.require_time === true,
          hasSignature: false,
          description: null,
          signature_base64: null,
          signature_user_id: null,
          signature_signed_at: null,
          zusatzaufgabenWarning: resolveRolliZusatzaufgabenWarning(rolli),
          due_date: dueDateValue,
          forceOverdue: isOverdue,

          estimatedDefekturMinutes: null,
          estimatedRegalpflegeMinutes: null,
          estimatedTotalMinutes: null,
          estimatesComputedAt: null,
          estimatesScope: null,
        } as Task;
      });

      return rolliTasks;
    } catch (error) {
      console.error('Fehler fetchRollis:', error);
      return [];
    }
  },

  /* -------------------------------------------------------------------------
   * CRUD / Mutationen
   * -------------------------------------------------------------------------*/
  setTasks: (tasks) => {
    set({ tasks });
    get().prepareDisplayedTasks();
  },

  addTask: (newTasks) => {
    set((state) => ({
      tasks: [...state.tasks, ...newTasks],
    }));
    get().prepareDisplayedTasks();
  },

  upsertTaskFromServer: (incomingTask) => {
    set((state) => {
      const existing = state.tasks.find((t) => t.id === incomingTask.id);
      const normalized = normalizeIncomingTask(incomingTask, existing);
      const tasks = existing
        ? state.tasks.map((t) => (t.id === normalized.id ? normalized : t))
        : [...state.tasks, normalized];
      return { tasks };
    });
    get().prepareDisplayedTasks();
  },

  updateTask: (taskId, updates) => {
    set((state) => ({
      tasks: state.tasks.map((t) => {
        if (t.id !== taskId) {
          return t;
        }

        const attachments =
          updates.attachments !== undefined ? updates.attachments ?? [] : t.attachments ?? [];

        const merged: Task = {
          ...t,
          ...updates,
          attachments,
        } as Task;

        if (updates.description === undefined) {
          merged.description = extractDescriptionFromAttachments(attachments) ?? null;
        }

        merged.require_sign = merged.require_sign ?? false;

        const normalizedSignature = normalizeSignatureInfo(merged);
        Object.assign(merged, normalizedSignature);

        return merged;
      }),
    }));
    get().prepareDisplayedTasks();
  },

  deleteTask: (taskId) => {
    set((state) => ({
      tasks: state.tasks.filter((t) => t.id !== taskId),
    }));
    get().prepareDisplayedTasks();
  },

  toggleComplete: (taskId, skipPrepare = false) => {
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === taskId ? { ...t, completed: !t.completed } : t
      ),
    }));
    // Nur ausführen, wenn skipPrepare nicht true ist
    if (!skipPrepare) {
      get().prepareDisplayedTasks();
    }
  },

  clearTasks: () => {
    set({
      tasks: [],
      overdueTasks: [],
      mainTasks: [],
    });
  },

  toggleShowCompleted: () => {
    set((s) => ({
      showCompleted: !s.showCompleted,
    }));
    get().prepareDisplayedTasks();
  },

  /**
   * prepareDisplayedTasks:
   * - Teilt die Aufgaben in "overdueTasks" und "mainTasks" auf
   * - Berücksichtigt forceOverdue
   * - Führt handleOverlapping für überfällige Aufgaben durch:
   *   => Wenn prevent_overlapping=true und dieselbe katalog_id, wird nur
   *      die neueste (größte due_date/time) beibehalten, ältere werden versteckt.
   * - Sortiert overdueTasks nach Zeit aufsteigend
   * - Sortiert mainTasks in Gruppen (<1h, keine due_time, plannerType, Rest)
   */
  prepareDisplayedTasks: () => {
    const { tasks, showCompleted } = get();
    const now = new Date();

    // 1) Filtern: versteckte Tasks raus, und erledigte nur, wenn showCompleted=false => raus
    const relevant = tasks.filter((t) => {
      if (t.hidden) return false;
      if (!showCompleted && t.completed) return false;
      return true;
    });

    // 2) Overdue vs. main
    const overdue: Task[] = [];
    const main: Task[] = [];

    for (const task of relevant) {
      if (task.forceOverdue || isTaskOverdue(task, now)) {
        overdue.push(task);
      } else {
        main.push(task);
      }
    }

    // 3) Overlapping nur bei den überfälligen Aufgaben
    const toHide = handleOverlapping(overdue);
    if (toHide.length > 0) {
      // Entfernt die versteckten Tasks aus dem Zustand
      set((state) => ({
        tasks: state.tasks.filter((t) => !toHide.includes(t.id)),
      }));
      // Entfernt sie auch lokal aus dem overdue-Array
      for (let i = overdue.length - 1; i >= 0; i--) {
        if (toHide.includes(overdue[i].id)) {
          overdue.splice(i, 1);
        }
      }
    }

    // 4) Sortierung overdueTasks nach Datum/Zeit
    overdue.sort((a, b) => parseDueDateTime(a).getTime() - parseDueDateTime(b).getTime());

    // 5) Sortierung mainTasks in Gruppen
    const sortedMain = main.slice().sort((a, b) => {
      const groupA = getHeuteSortGroup(a, now);
      const groupB = getHeuteSortGroup(b, now);
      if (groupA !== groupB) {
        return groupA - groupB;
      }
      return parseDueDateTime(a).getTime() - parseDueDateTime(b).getTime();
    });

    // 6) In den Zustand schreiben
    set({
      overdueTasks: overdue,
      mainTasks: sortedMain,
    });
  },

  // Planner-Tasks lokal löschen
  deletePlannerTask: (taskId) => {
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== taskId) }));
    get().prepareDisplayedTasks();
  },

  // Rolli-Tasks lokal löschen
  deleteRolliTask: (taskId) => {
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== taskId) }));
    get().prepareDisplayedTasks();
  },

  /**
   * cleanupOldTasks:
   * Entfernt alles, was älter als 6 Monate ist
   */
  cleanupOldTasks: () => {
    set((state) => {
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - SIX_MONTHS);
      const filtered = state.tasks.filter((t) => {
        const dt = parseDueDateTime(t);
        return isAfter(dt, cutoff);
      });
      return { tasks: filtered };
    });
    get().prepareDisplayedTasks();
  },

  /**
   * initializeSocketListeners:
   * Registriert WebSocket-Event-Handler für Echtzeit-Updates
   */
  initializeSocketListeners: () => {
    // Event-Handler für Task-Abschluss
    const unsubscribeTaskCompleted = appSocketService.onTaskUpdate((data: unknown) => {
      console.log('📡 Socket: Task abgeschlossen erhalten:', data);

      if (isTaskSocketPayload(data) && data.task) {
        const { task } = data;

        // Task im Store aktualisieren
        get().updateTask(task.id, {
          completed: true,
          completed_date: task.completed_date,
          hasSignature: task.hasSignature,
          signature_user_id: task.signature_user_id,
          signature_signed_at: task.signature_signed_at,
          signature_base64: task.signature_base64 || null,
        });

        console.log(`✅ Task ${task.id} wurde über WebSocket als abgeschlossen markiert`);
      }
    });

    // Event-Handler für generische Task-Updates (create, update, delete)
    const unsubscribeTaskUpdate = appSocketService.onTaskUpdate((data: unknown) => {
      console.log('📡 Socket: Task-Update erhalten:', data);

      if (isTaskSocketPayload(data) && data.type && data.entityId) {
        const { type, entityId, storeId } = data;

        // Bei Updates die Task-Daten neu laden (falls es sich um den aktuellen Store handelt)
        const { selectedStoreId } = useAuthStore.getState();
        if (storeId === selectedStoreId) {
          const { loadAllData, deleteTask } = get();
          switch (type) {
            case 'create':
              console.log(`📋 Neuer Task ${entityId} erstellt - lade Tasks neu`);
              if (typeof loadAllData === 'function') {
                void loadAllData(storeId, "incomplete", 365);
              }
              break;
            case 'update':
              console.log(`📝 Task ${entityId} aktualisiert - lade Tasks neu`);
              if (typeof loadAllData === 'function') {
                void loadAllData(storeId, "incomplete", 365);
              }
              break;
            case 'delete':
              console.log(`🗑️ Task ${entityId} gelöscht - entferne aus Store`);
              if (typeof deleteTask === 'function') {
                deleteTask(entityId);
              }
              break;
          }
        }
      }
    });

    // Cleanup-Funktion zurückgeben
    return () => {
      if (typeof unsubscribeTaskCompleted === 'function') {
        (unsubscribeTaskCompleted as () => void)();
      }
      if (typeof unsubscribeTaskUpdate === 'function') {
        (unsubscribeTaskUpdate as () => void)();
      }
      // Fallback: alle Listener entfernen, falls der Service keine Unsubscribe-Funktion liefert
      appSocketService.offTaskUpdate();
    };
  },
}));

/* -------------------------------------------------------------------------
 * Hilfsfunktionen
 * -------------------------------------------------------------------------*/

function extractDescriptionFromAttachments(
  attachments?: AllRoutesTypes.Attachment[] | null
): string | null {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  const descriptions = attachments
    .filter((attachment) => attachment.typ === AllRoutesTypes.AttachmentType.description)
    .map((attachment) => {
      const data = attachment.data;
      if (typeof data === 'string') {
        return data;
      }
      if (Array.isArray(data)) {
        return '';
      }
      if (data && typeof data === 'object' && 'text' in data) {
        const textValue = (data as { text?: unknown }).text;
        return typeof textValue === 'string' ? textValue : '';
      }
      return '';
    })
    .filter((value): value is string => Boolean(value && value.trim().length > 0));

  if (descriptions.length === 0) {
    return null;
  }

  return descriptions.join('\n\n');
}

function normalizeIncomingTask(
  incoming: Partial<Task> & { id: string },
  existing?: Task
): Task {
  const attachments =
    incoming.attachments !== undefined
      ? incoming.attachments ?? []
      : existing?.attachments ?? [];

  const merged: Task = {
    ...(existing ?? ({} as Task)),
    ...incoming,
    attachments,
  };

  if (incoming.description === undefined) {
    const description = extractDescriptionFromAttachments(attachments) ?? null;
    merged.description = existing?.description ?? description;
  }

  merged.require_sign = merged.require_sign ?? existing?.require_sign ?? false;
  merged.require_time = merged.require_time ?? existing?.require_time ?? false;

  const normalizedSignature = normalizeSignatureInfo(merged);
  Object.assign(merged, normalizedSignature);

  return merged;
}

type SignatureInfoSource = {
  hasSignature?: boolean | null;
  signature_base64?: string | null;
  signature_signed_at?: string | null;
  signature_user_id?: string | null;
};

type NormalizedSignatureInfo = {
  hasSignature: boolean;
  signature_base64: string | null;
  signature_signed_at: string | null;
  signature_user_id: string | null;
};

function normalizeSignatureInfo(source: SignatureInfoSource): NormalizedSignatureInfo {
  const signature_base64 = source.signature_base64 ?? null;
  const signature_signed_at = source.signature_signed_at ?? null;
  const signature_user_id = source.signature_user_id ?? null;

  let hasSignature = source.hasSignature ?? false;
  if (signature_signed_at || signature_base64) {
    hasSignature = true;
  }

  return {
    hasSignature,
    signature_base64,
    signature_signed_at,
    signature_user_id,
  };
}

/**
 * isTaskOverdue:
 * Bestimmt, ob eine Aufgabe "überfällig" ist.
 * => Falls due_date/time < jetzt => true
 * => Falls forceOverdue = true => true
 * => Spezialfall für Rollis: Wenn ein Rolli ein due_date in der Vergangenheit hat,
 *    ist er überfällig, auch ohne due_time
 */
function isTaskOverdue(task: Task, now: Date): boolean {
  if (task.forceOverdue) return true;

  // Spezialfall für Rollis: Wenn due_date in der Vergangenheit liegt, ist der Rolli überfällig
  if (task.isRolli && task.due_date) {
    const dueDate = new Date(task.due_date);
    const today = startOfDay(now);
    if (dueDate < today) {
      return true; // Rolli mit due_date in der Vergangenheit ist überfällig
    }
  }

  const dt = parseDueDateTime(task);
  return dt.getTime() < now.getTime() - OVERDUE_BUFFER * 60000;
}

/**
 * parseDueDateTime:
 * Parst (task.due_date + task.due_time) als LOKALE Zeit zu einem Date-Objekt.
 * Falls due_date nicht vorhanden => sehr weit in der Zukunft.
 *
 * Damit "2025-03-03T08:00" (ohne Zeitzone) auch wirklich 08:00 lokale Zeit wird,
 * bauen wir das Datum manuell zusammen (year, month, day, hour, minute, second).
 */
function parseDueDateTime(task: Task): Date {
  if (!task.due_date) {
    // Kein Datum => niemals überfällig
    return new Date('9999-12-31T23:59:59');
  }

  const [yearStr, monthStr, dayStr] = task.due_date.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1; // JS: 0-basiert
  const day = parseInt(dayStr, 10);

  let hours = 23;
  let minutes = 59;
  let seconds = 59;

  if (task.due_time && task.due_time.trim() !== '') {
    const parts = task.due_time.split(':');
    if (parts[0]) hours = parseInt(parts[0], 10);
    if (parts[1]) minutes = parseInt(parts[1], 10);
    if (parts[2]) seconds = parseInt(parts[2], 10);
  }

  return new Date(year, month, day, hours, minutes, seconds);
}

/**
 * getHeuteSortGroup:
 * Gibt eine Zahl zurück, anhand derer mainTasks sortiert werden:
 * - 0 => in <1 Stunde fällig
 * - 1 => keine due_time
 * - 2 => plannerType (MHD, Zählung, Umbau)
 * - 3 => Rest
 */
function getHeuteSortGroup(task: Task, now: Date): number {
  const dt = parseDueDateTime(task);
  const inOneHour = addMinutes(now, ONE_HOUR);

  // <1h ?
  if (dt.getTime() <= inOneHour.getTime()) {
    return 0;
  }
  // Keine due_time ?
  if (!task.due_time || !task.due_time.trim()) {
    return 1;
  }
  // Planner-Item (mhd, zählung, umbau)?
  if (task.plannerType) {
    return 2;
  }
  // Sonst
  return 3;
}

/**
 * handleOverlapping:
 * Sucht in einem Array von überfälligen Tasks nach doppelten katalog_id-Werten
 * mit prevent_overlapping = true. Hält nur die jeweils neueste (größtes Datum)
 * und gibt die IDs der älteren Tasks zurück, die versteckt werden sollen.
 * 
 * Gleichzeitig werden diese älteren Tasks per PUT /api/tasks/<id> (hidden=true) an
 * das Backend gemeldet (hideTaskOnServer).
 */
function handleOverlapping(overdueTasks: Task[]): string[] {
  const groups: Record<string, Task[]> = {};

  // 1) Gruppen bilden nach katalog_id
  for (const t of overdueTasks) {
    if (t.prevent_overlapping && t.katalog_id) {
      const kId = t.katalog_id;
      if (!groups[kId]) {
        groups[kId] = [];
      }
      groups[kId].push(t);
    }
  }

  // 2) Herausfinden, welche Tasks pro Gruppe ausgeblendet werden sollen
  const toHide: string[] = [];

  for (const kId of Object.keys(groups)) {
    const group = groups[kId];
    if (group.length < 2) continue;

    // Sortierung nach Datum/Zeit DESC (neuestes zuerst)
    group.sort((a, b) => parseDueDateTime(b).getTime() - parseDueDateTime(a).getTime());

    // Der erste Eintrag (Index 0) ist der neueste => den behalten
    const newest = group[0];

    // Alle weiteren (älteren) verstecken
    for (let i = 1; i < group.length; i++) {
      const oldTask = group[i];
      toHide.push(oldTask.id);
      hideTaskOnServer(oldTask.id).catch((err) => {
        console.warn(`Fehler hideTaskOnServer(${oldTask.id}):`, err);
      });
    }

    console.log(
      `Overlapping erkannt für katalog_id=${kId}. Behalte '${newest.id}', verstecke ${group.length - 1} ältere.`
    );
  }

  return toHide;
}

/**
 * hideTaskOnServer:
 * Setzt hidden=true per PUT /api/tasks/<taskId> im Backend.
 */
async function hideTaskOnServer(taskId: string): Promise<void> {
  await httpClient.put(`/api/tasks/${taskId}`, { hidden: true });
}

/**
 * mergeById:
 * Nimmt ein Array von Tasks und entfernt Duplikate (gleiche ID).
 */
function mergeById(arr: Task[]): Task[] {
  const map = new Map<string, Task>();
  for (const t of arr) {
    if (!map.has(t.id)) {
      map.set(t.id, t);
    }
  }
  return Array.from(map.values());
}

async function applyRolliTimeEstimatesToTasks(storeId: string, tasks: Task[]): Promise<Task[]> {
  try {
    const response = await fetchWithAuth(`/api/rolli-time-estimates?storeId=${storeId}`);
    if (!response.ok) return tasks;
    const payload: unknown = await response.json();
    if (!isRolliTimeEstimatesPayload(payload) || !payload.estimates) return tasks;

    const estimates = payload.estimates;
    const scope: 'store' | 'cross_market' | null = payload.scope ?? null;
    const computedAt: string | null = payload.computedAt ?? null;

    return tasks.map((task) => {
      if (!task.isRolli || !task.rolliId) return task;
      const entry = estimates[task.rolliId];
      if (!entry) return task;

      return {
        ...task,
        estimatedDefekturMinutes: entry?.defektur?.medianMinutes ?? null,
        estimatedRegalpflegeMinutes: entry?.regalpflege?.medianMinutes ?? null,
        estimatedTotalMinutes: entry?.totalMedianMinutes ?? null,
        estimatesComputedAt: computedAt,
        estimatesScope: scope,
      };
    });
  } catch (error) {
    console.warn('applyRolliTimeEstimatesToTasks: Fehler beim Laden der Zeitvorgaben', error);
    return tasks;
  }
}

/**
 * Socket-Event-Handler für Task-Updates initialisieren
 * Diese Funktion wird von der Tasks-Komponente aufgerufen
 */
export function initializeTaskSocketListeners(): () => void {
  const handleTaskUpdate = (data: unknown) => {
    if (!isTaskSocketPayload(data) || !data.type || !data.storeId) {
      return;
    }

    const { selectedStoreId: currentStoreId } = useAuthStore.getState();
    if (!currentStoreId || String(currentStoreId) !== String(data.storeId)) {
      return;
    }

    const taskStore = useTaskStore.getState();
    const eventType = data.type as string;

    if (['create', 'update', 'completed'].includes(eventType) && data.task) {
      taskStore.upsertTaskFromServer(data.task);
      return;
    }

    if (eventType === 'delete' && data.entityId) {
      taskStore.deleteTask(data.entityId);
    }
  };

  appSocketService.onTaskUpdate(handleTaskUpdate);
  return () => {
    appSocketService.offTaskUpdate(handleTaskUpdate);
  };
}
